import Head from 'next/head'
import { useEffect } from 'react'
import { usePlace } from 'store/usePlace'
import { CustomisationData, LightThemeConfig, DarkThemeConfig } from '@2meters/types'

export const CssCustomisation = () => {
  // sets css vars that are then use in tailwind config
  const { place } = usePlace()

  function hexToRgba(hex: string, alpha: number = 1): string {
    hex = hex.replace('#', '')
    const bigint = parseInt(hex, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return `rgba(${r},${g},${b},${alpha})`
  }

  function hexToHsl(hex: string): [number, number, number] {
    hex = hex.replace('#', '')
    const bigint = parseInt(hex, 16)
    const r = ((bigint >> 16) & 255) / 255
    const g = ((bigint >> 8) & 255) / 255
    const b = (bigint & 255) / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min
    let h = 0
    let s = 0
    const l = (max + min) / 2
    if (delta !== 0) {
      s = delta / (1 - Math.abs(2 * l - 1))
      if (max === r) h = ((g - b) / delta + 6) % 6
      else if (max === g) h = (b - r) / delta + 2
      else h = (r - g) / delta + 4
      h = h / 6
    }
    return [h, s, l]
  }

  function hslToHex(h: number, s: number, l: number): string {
    const a = s * Math.min(l, 1 - l)
    const f = (n: number) => {
      const k = (n + h * 12) % 12
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, '0')
    }
    return `#${f(0)}${f(8)}${f(4)}`
  }

  function wcagLuminance(hex: string): number {
    hex = hex.replace('#', '')
    const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
    const bigint = parseInt(hex, 16)
    const r = toLinear(((bigint >> 16) & 255) / 255)
    const g = toLinear(((bigint >> 8) & 255) / 255)
    const b = toLinear((bigint & 255) / 255)
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }

  function contrastRatio(hex1: string, hex2: string): number {
    const l1 = wcagLuminance(hex1)
    const l2 = wcagLuminance(hex2)
    const lighter = Math.max(l1, l2)
    const darker = Math.min(l1, l2)
    return (lighter + 0.05) / (darker + 0.05)
  }

  /**
   * Returns a hue-preserving color derived from buttonColor that achieves at
   * least minRatio contrast against bgColor. If the original already passes,
   * it is returned unchanged. Only lightness is shifted; saturation is boosted
   * slightly when lightening to avoid washed-out pastels.
   */
  function ensureContrast(buttonColor: string, bgColor: string, minRatio = 4.5): string {
    if (contrastRatio(buttonColor, bgColor) >= minRatio) return buttonColor
    const bgLum = wcagLuminance(bgColor)
    // Dark background → lighten accent; light background → darken accent
    const lighten = bgLum < 0.18
    const [h, s, l] = hexToHsl(buttonColor)
    const target = lighten ? 1 : 0
    // Binary search for the smallest shift that meets the contrast requirement
    let lo = l
    let hi = target
    let best = lighten ? 1 : 0
    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2
      // Boost saturation slightly when lightening to keep color vivid
      const sAdj = lighten && mid > 0.5 ? Math.min(1, s + (mid - 0.5) * 0.4) : s
      const candidate = hslToHex(h, sAdj, mid)
      if (contrastRatio(candidate, bgColor) >= minRatio) {
        best = mid
        hi = mid
      } else {
        lo = mid
      }
    }
    const sAdj = lighten && best > 0.5 ? Math.min(1, s + (best - 0.5) * 0.4) : s
    return hslToHex(h, sAdj, best)
  }

  /**
   * Returns a greyscale (saturation=0) text color with WCAG AA (4.5:1) contrast
   * against bgHex. Two separate strategies are used:
   *
   * Dark background: starts from white and steps toward black only if white
   *   somehow fails (almost never happens — white on dark bg always clears 4.5:1).
   *   Result is always white or very near-white.
   *
   * Light background: desaturates the intended rawTextColor (strips hue, keeps
   *   lightness) to get a natural-feeling greyscale, then verifies 4.5:1. If the
   *   desaturated value fails (e.g. a custom bg that is darker than expected),
   *   binary-searches toward black to find the lightest gray that still passes —
   *   producing a comfortable dark gray rather than forcing pure black.
   */
  function resolveTextColor(rawTextColor: string, bgHex: string, dark: boolean): string {
    if (dark) {
      // Dark background → white text
      if (contrastRatio('#ffffff', bgHex) >= 4.5) return '#ffffff'
      // Edge case: very light "dark" bg — find lightest passing gray
      let lo = 0,
        hi = 1,
        best = 0
      for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) / 2
        if (contrastRatio(hslToHex(0, 0, mid), bgHex) >= 4.5) {
          best = mid
          lo = mid
        } else hi = mid
      }
      return hslToHex(0, 0, best)
    } else {
      // Light background → desaturate rawTextColor to greyscale
      const [, , l] = hexToHsl(rawTextColor)
      const desaturated = hslToHex(0, 0, l)
      if (contrastRatio(desaturated, bgHex) >= 4.5) return desaturated
      // Bg darker than expected: binary-search for the lightest gray in [0, l]
      // that still meets 4.5:1 — avoids jumping straight to pure black
      let lo = 0,
        hi = l,
        best = 0
      for (let i = 0; i < 24; i++) {
        const mid = (lo + hi) / 2
        if (contrastRatio(hslToHex(0, 0, mid), bgHex) >= 4.5) {
          best = mid
          lo = mid
        } else hi = mid
      }
      return hslToHex(0, 0, best)
    }
  }

  function _createTints(
    h: number,
    s: number,
    bgLum: number,
    isDark: boolean
  ): [string, string, string] {
    if (isDark) {
      // Dark theme: visible dim tints, lighter than bg, darker than button
      return [
        hslToHex(h, Math.min(1, s * 0.55), 0.15),
        hslToHex(h, Math.min(1, s * 0.65), 0.22),
        hslToHex(h, Math.min(1, s * 0.72), 0.28),
      ]
    } else {
      // Light theme: very pale pastels using button hue.
      // When bg is darker, push L higher so selection is always distinctly lighter.
      // (1 - bgLum) ranges 0→1 as bg goes white→black, adding up to +0.06 to L.
      const lift = (1 - bgLum) * 0.06
      return [
        hslToHex(h, Math.min(1, s * 0.4), Math.min(0.97, 0.94 + lift)),
        hslToHex(h, Math.min(1, s * 0.55), Math.min(0.97, 0.91 + lift)),
        hslToHex(h, Math.min(1, s * 0.65), Math.min(0.97, 0.87 + lift)),
      ]
    }
  }

  useEffect(() => {
    const body = document.body
    if (place?.customisation.theme !== 'dark') {
      console.log("Use theme 'light'")
      body.classList.remove('dark')
      document.documentElement.classList.remove('dark')
    } else {
      console.log("Use theme 'dark'")
      body.classList.add('dark')
      document.documentElement.classList.add('dark')
    }
  }, [place])

  /**
   * Resolves the active theme's color config from customisation data.
   *
   * Priority:
   *  1. v2 per-theme sub-object (customisation.light / customisation.dark)
   *  2. Legacy v1 flat fields on the active theme
   *  3. Hard-coded defaults (LightThemeConfig / DarkThemeConfig)
   */
  function resolveColors(c: CustomisationData | undefined, isDark: boolean) {
    const defaults = isDark ? DarkThemeConfig : LightThemeConfig
    const themeKey = isDark ? 'dark' : 'light'

    // v2: explicit per-theme sub-object
    if (c?.[themeKey]) {
      return {
        primaryColor: c[themeKey]!.primaryColor ?? defaults.primaryColor!,
        secondaryColor: c[themeKey]!.secondaryColor ?? defaults.secondaryColor!,
        backgroundColor: c[themeKey]!.backgroundColor ?? defaults.backgroundColor!,
        textColor: c[themeKey]!.textColor ?? defaults.textColor!,
      }
    }

    // v1 legacy: flat fields (only used when the stored theme matches the active one,
    // or when there is no stored theme field)
    if (c?.buttonColor || c?.headerColor || c?.backgroundColor) {
      return {
        // buttonColor → primaryColor, headerColor → secondaryColor
        primaryColor: c.buttonColor ?? defaults.primaryColor!,
        secondaryColor: c.headerColor ?? defaults.secondaryColor!,
        backgroundColor: c.backgroundColor ?? defaults.backgroundColor!,
        textColor: c.textColor ?? defaults.textColor!,
      }
    }

    return {
      primaryColor: defaults.primaryColor!,
      secondaryColor: defaults.secondaryColor!,
      backgroundColor: defaults.backgroundColor!,
      textColor: defaults.textColor!,
    }
  }

  const isDark = place?.customisation?.theme === 'dark'
  const {
    primaryColor,
    secondaryColor,
    backgroundColor,
    textColor: rawTextColor,
  } = resolveColors(place?.customisation, isDark)

  // Internal aliases kept for readability of the CSS var block below.
  // CSS variable names are documented below; --color-header kept for backwards
  // backwards compatibility with the book app's Tailwind config.
  const buttonColor = primaryColor // → --color-primary
  const headerColor = secondaryColor // → --color-header / --color-secondary*

  // minRatio 3.0 = WCAG AA for UI components/icons; preserves buttonColor on white bg
  const accentColor = ensureContrast(buttonColor, backgroundColor, 3.0)
  // Greyscale WCAG AA text color: white on dark backgrounds, natural dark gray on light.
  // Always saturation=0 — see resolveTextColor() above.
  const textColor = resolveTextColor(rawTextColor, backgroundColor, isDark)
  // Muted text: lighter than the main text color to match step-description style.
  // Light theme targets ~gray-600 (L≈0.41); dark theme targets ~gray-200 (L≈0.86).
  // Both are clamped to WCAG AA (4.5:1) against the background.
  const textColorMuted = resolveTextColor(
    isDark ? '#dbdbdb' : '#4b4b4b', // starting points: near gray-200 / near gray-600
    backgroundColor,
    isDark
  )
  const [hh, hs, hl] = hexToHsl(headerColor)
  // All three tints use headerColor — no flash on hover/tap
  const hoverTint = headerColor
  const selectedTint = headerColor
  const activeTint = headerColor
  // Border ring for radio bullets: darker on light theme, lighter on dark theme
  const elementBorderColor = isDark
    ? hslToHex(hh, hs, Math.min(1, hl + 0.3))
    : hslToHex(hh, hs, Math.max(0, hl - 0.25))

  return (
    <>
      <Head>
        <style>
          :root{' '}
          {`{ 
            --color-bg: ${backgroundColor};
            --color-header: ${headerColor};
            --color-primary: ${hexToRgba(buttonColor, 1)};
            --color-accent: ${accentColor};
            --color-secondary: ${hexToRgba(headerColor, 1)};
            --color-secondary_hover: ${hoverTint};
            --color-secondary_selected: ${selectedTint};
            --color-secondary_active: ${activeTint};
            --color-secondary_border: ${elementBorderColor};
            --color-secondary_hover_dark: color-mix(in srgb, var(--color-secondary) 85%, white);
            --color-text: ${textColor};
            --color-text-muted: ${textColorMuted};
            --color-input-bg: color-mix(in srgb, var(--color-bg) 80%, black);
          }`}
        </style>
      </Head>
    </>
  )
}
